import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, 
  Search, 
  Plus, 
  Filter,
  Download,
  MoreVertical,
  TrendingUp,
  Activity,
  Clock,
  AlertCircle,
  CheckCircle,
  Eye,
  Edit,
  Send,
  Sparkles,
  Award,
  Calendar,
  Users,
  Bot,
  Brain,
  Zap,
  Target,
  BarChart3,
  RefreshCw,
  Settings,
  Archive,
  Star,
  PlayCircle,
  PauseCircle,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Button } from '../../components/ui/Button/Button';
import { cn } from '../../utils/cn';
import { useConversationWebSocket } from '../../hooks/useConversationWebSocket';
import { AIResponsePanel } from './components/AIResponsePanel';
import { ConversationAnalytics } from './components/ConversationAnalytics';
import { RealTimeConversationView } from './components/RealTimeConversationView';
import { ConversationFilters } from './components/ConversationFilters';

// Enhanced conversation interface with AI response management
interface EnhancedConversation {
  id: string;
  conversationId: string;
  contact: {
    name: string;
    email: string;
    company?: string;
    avatar?: string;
  };
  lastMessage: {
    content: string;
    timestamp: string;
    isOutgoing: boolean;
  };
  status: 'active' | 'positive' | 'high-priority' | 'needs-response' | 'closed' | 'escalated' | 'ai-processing';
  priority: 'low' | 'medium' | 'high' | 'critical';
  campaign?: string;
  messageCount: number;
  unreadCount: number;
  labels: string[];
  aiAnalysis?: {
    sentiment: string;
    intent: string;
    confidence: number;
    entities: any[];
    businessContext: any;
    riskFactors: string[];
  };
  suggestedResponses?: any[];
  escalationRecommendation?: any;
  metadata: {
    traceId?: string;
    confidence?: number;
    processedBy?: string;
    processingTime?: number;
  };
}

interface ConversationManagementState {
  conversations: EnhancedConversation[];
  selectedConversation: EnhancedConversation | null;
  conversationContext: any | null;
  pendingResponses: any[];
  activeGenerations: any[];
  responseQueue: any[];
  filters: {
    status: string[];
    priority: string[];
    channel: string[];
    campaign: string[];
    assignee: string[];
    dateRange: { start: Date | null; end: Date | null };
    sentiment: string[];
    requiresAttention: boolean;
    aiHandled: boolean;
    humanReviewRequired: boolean;
  };
  realTimeEnabled: boolean;
  lastUpdate: Date;
  pendingUpdates: number;
  viewMode: 'list' | 'kanban' | 'timeline';
  sidebarOpen: boolean;
  selectedTab: 'overview' | 'messages' | 'analysis' | 'responses' | 'performance';
  analytics: any;
  performanceMetrics: any;
}

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number; prefix?: string; suffix?: string }> = ({ 
  end, 
  duration = 2000, 
  prefix = "", 
  suffix = ""
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return (
    <span className="font-bold">
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

// Enhanced Metric Card Component with real-time updates
const EnhancedMetricCard: React.FC<{
  title: string;
  value: string | number;
  change: string;
  changeType: 'up' | 'down' | 'neutral';
  icon: React.ElementType;
  gradient: string;
  description?: string;
  realTime?: boolean;
  onClick?: () => void;
}> = ({ title, value, change, changeType, icon: Icon, gradient, description, realTime, onClick }) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (realTime) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [value, realTime]);

  const ChangeIcon = changeType === 'up' ? ArrowUp : changeType === 'down' ? ArrowDown : ArrowRight;
  const changeColor = changeType === 'up' ? 'text-green-400' : changeType === 'down' ? 'text-red-400' : 'text-yellow-400';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0, scale: isAnimating ? 1.02 : 1 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className={cn(
        "group relative p-6 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-xl hover:shadow-2xl cursor-pointer",
        onClick && "hover:cursor-pointer"
      )}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-neutral-400 text-sm font-medium mb-2 flex items-center">
            {title}
            {realTime && (
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="ml-2"
              >
                <Wifi className="w-3 h-3 text-green-400" />
              </motion.div>
            )}
          </p>
          <p className="text-3xl font-bold text-white mb-1">
            {typeof value === 'number' ? (
              <AnimatedCounter end={value} />
            ) : (
              value
            )}
          </p>
          <div className="flex items-center space-x-2">
            <span className={cn("text-sm font-medium flex items-center", changeColor)}>
              <ChangeIcon className="w-3 h-3 mr-1" />
              {change}
            </span>
          </div>
          {description && (
            <p className="text-neutral-500 text-xs mt-2">{description}</p>
          )}
        </div>
        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", gradient)}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* Real-time pulse effect */}
      {realTime && isAnimating && (
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl animate-pulse" />
      )}
    </motion.div>
  );
};

// Enhanced Conversation Card with AI insights
const EnhancedConversationCard: React.FC<{
  conversation: EnhancedConversation;
  isSelected: boolean;
  onClick: () => void;
}> = ({ conversation, isSelected, onClick }) => {
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          color: 'text-green-400 bg-green-500/20 border-green-500/30',
          dot: 'bg-green-400',
          label: 'Active',
          icon: Activity
        };
      case 'positive':
        return {
          color: 'text-blue-400 bg-blue-500/20 border-blue-500/30',
          dot: 'bg-blue-400',
          label: 'Positive',
          icon: CheckCircle
        };
      case 'high-priority':
        return {
          color: 'text-red-400 bg-red-500/20 border-red-500/30',
          dot: 'bg-red-400',
          label: 'High Priority',
          icon: AlertCircle
        };
      case 'needs-response':
        return {
          color: 'text-orange-400 bg-orange-500/20 border-orange-500/30',
          dot: 'bg-orange-400',
          label: 'Needs Response',
          icon: Clock
        };
      case 'escalated':
        return {
          color: 'text-purple-400 bg-purple-500/20 border-purple-500/30',
          dot: 'bg-purple-400',
          label: 'Escalated',
          icon: Users
        };
      case 'ai-processing':
        return {
          color: 'text-cyan-400 bg-cyan-500/20 border-cyan-500/30',
          dot: 'bg-cyan-400',
          label: 'AI Processing',
          icon: Bot
        };
      default:
        return {
          color: 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30',
          dot: 'bg-neutral-400',
          label: 'Closed',
          icon: Archive
        };
    }
  };

  const statusConfig = getStatusConfig(conversation.status);
  const initials = (conversation.contact?.name || 'U')
    .split(' ')
    .map(n => n.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();

  const StatusIcon = statusConfig.icon;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      whileHover={{ x: 4 }}
      onClick={onClick}
      className={cn(
        "p-4 border-b border-neutral-700/50 cursor-pointer transition-all duration-200 hover:bg-neutral-800/30",
        isSelected && "bg-blue-500/10 border-blue-500/30"
      )}
    >
      <div className="flex items-start space-x-4">
        {/* Avatar with AI confidence indicator */}
        <div className="relative flex-shrink-0">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-semibold">{initials}</span>
          </div>
          <div className={cn("absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-neutral-900 flex items-center justify-center", statusConfig.dot)}>
            <StatusIcon className="w-2 h-2 text-white" />
          </div>
          {conversation.aiAnalysis && (
            <div className="absolute -top-1 -left-1 w-5 h-5 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center">
              <Brain className="w-3 h-3 text-white" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-medium text-white truncate flex items-center">
              {conversation.contact.name}
              {conversation.aiAnalysis && conversation.aiAnalysis.confidence > 0.8 && (
                <Sparkles className="w-3 h-3 ml-1 text-cyan-400" />
              )}
            </h3>
            <span className="text-xs text-neutral-400">
              {new Date(conversation.lastMessage.timestamp).toLocaleDateString()}
            </span>
          </div>

          <div className="flex items-center space-x-2 mb-2 flex-wrap">
            <span className={cn(
              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border backdrop-blur-xl",
              statusConfig.color
            )}>
              <StatusIcon className="w-2 h-2 mr-1" />
              {statusConfig.label}
            </span>
            
            {conversation.campaign && (
              <span className="px-2 py-1 bg-neutral-700/50 text-neutral-300 text-xs rounded-full">
                {conversation.campaign}
              </span>
            )}

            {conversation.aiAnalysis && (
              <span className="px-2 py-1 bg-cyan-500/20 text-cyan-300 text-xs rounded-full border border-cyan-500/30">
                {Math.round(conversation.aiAnalysis.confidence * 100)}% AI
              </span>
            )}
          </div>

          <p className="text-sm text-neutral-400 line-clamp-2 mb-2">
            {conversation.lastMessage.content}
          </p>

          {/* AI Analysis Summary */}
          {conversation.aiAnalysis && (
            <div className="flex items-center space-x-3 text-xs text-neutral-500 mb-2">
              <span className="flex items-center">
                <Target className="w-3 h-3 mr-1" />
                {conversation.aiAnalysis.intent}
              </span>
              <span className="flex items-center">
                <Activity className="w-3 h-3 mr-1" />
                {conversation.aiAnalysis.sentiment}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between text-xs text-neutral-500">
            <span className="flex items-center space-x-2">
              <span>{conversation.messageCount} messages</span>
              {conversation.suggestedResponses && conversation.suggestedResponses.length > 0 && (
                <span className="flex items-center text-cyan-400">
                  <Bot className="w-3 h-3 mr-1" />
                  {conversation.suggestedResponses.length} responses
                </span>
              )}
            </span>
            {conversation.unreadCount > 0 && (
              <span className="px-2 py-1 bg-blue-500 text-white rounded-full">
                {conversation.unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const ConversationsManagementPage: React.FC = () => {
  const [state, setState] = useState<ConversationManagementState>({
    conversations: [],
    selectedConversation: null,
    conversationContext: null,
    pendingResponses: [],
    activeGenerations: [],
    responseQueue: [],
    filters: {
      status: [],
      priority: [],
      channel: [],
      campaign: [],
      assignee: [],
      dateRange: { start: null, end: null },
      sentiment: [],
      requiresAttention: false,
      aiHandled: false,
      humanReviewRequired: false
    },
    realTimeEnabled: true,
    lastUpdate: new Date(),
    pendingUpdates: 0,
    viewMode: 'list',
    sidebarOpen: true,
    selectedTab: 'overview',
    analytics: null,
    performanceMetrics: null
  });

  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dashboardStats, setDashboardStats] = useState<any>({
    aiResponseRate: 0,
    activeConversations: 6,
    needsAttention: 0,
    avgResponseTime: '0s',
    confidenceScore: 95,
    processingQueue: 3,
    escalations: 1,
    responseAccuracy: 98
  });
  
  // Advanced AI features
  const [aiAutopilot, setAIAutopilot] = useState(false);
  const [showAdvancedMetrics, setShowAdvancedMetrics] = useState(false);
  const [aiLearningMode, setAILearningMode] = useState(true);

  // WebSocket connection for real-time updates
  const {
    isConnected,
    joinConversation,
    leaveConversation,
    generateResponse,
    sendResponse,
    requestEscalation
  } = useConversationWebSocket({
    onConversationUpdate: handleConversationUpdate,
    onResponseGenerated: handleResponseGenerated,
    onEscalationTriggered: handleEscalationTriggered,
    onDashboardStats: handleDashboardStats
  });

  // Enhanced data loading with real AI integration
  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const [conversationsResponse, overviewResponse, aiMetricsResponse] = await Promise.all([
        fetch('http://localhost:3001/api/conversations?include_ai_analysis=true', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('http://localhost:3001/api/conversations/management/dashboard/overview', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch('http://localhost:3001/api/ai/metrics', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (conversationsResponse.ok) {
      const conversationsResult = await conversationsResponse.json();
      if (conversationsResult.success && conversationsResult.data) {
        setState(prev => ({
          ...prev,
          conversations: conversationsResult.data.conversations.map(enhanceConversation)
        }));

        if (conversationsResult.data.conversations.length > 0) {
          setState(prev => ({
            ...prev,
            selectedConversation: enhanceConversation(conversationsResult.data.conversations[0])
          }));
        }
      }
      } else {
                 // Enhanced demo data for AI management
         const demoConversations: EnhancedConversation[] = [
           {
             id: 'conv-ai-1',
             conversationId: 'conv-1',
             contact: {
               name: 'Andrewjohnvercueil',
               email: 'andrewjohnvercueil@gmail.com',
               company: 'TechCorp Inc.'
             },
             lastMessage: {
               content: 'Email sent via workflow: New Workflow - AI response pending',
               timestamp: new Date().toISOString(),
               isOutgoing: true
             },
             status: 'ai-processing' as const,
             priority: 'high' as const,
             campaign: 'AI Demo Campaign',
             messageCount: 3,
             unreadCount: 1,
             labels: ['ai-generated', 'workflow', 'priority'],
             aiAnalysis: {
               sentiment: 'positive',
               intent: 'product_inquiry',
               confidence: 0.92,
               entities: ['product', 'pricing', 'demo'],
               businessContext: { value: 'high', engagement: 'active' },
               riskFactors: []
             },
             suggestedResponses: [
               { id: 'resp-1', content: 'Thank you for your interest! Let me schedule a personalized demo for you.', confidence: 0.95 },
               { id: 'resp-2', content: 'I\'d be happy to show you our revolutionary features. When works best for you?', confidence: 0.88 }
             ],
             metadata: {
               traceId: 'trace-123',
               confidence: 0.92,
               processedBy: 'ai-engine-v2',
               processingTime: 1.2
             }
           }
         ];
        
        setState(prev => ({ ...prev, conversations: demoConversations }));
        if (demoConversations.length > 0) {
          setState(prev => ({ ...prev, selectedConversation: demoConversations[0] }));
        }
      }

      // Update stats with real-time demo values
      setDashboardStats({
        aiResponseRate: 65,
        activeConversations: 6,
        needsAttention: 2,
        avgResponseTime: '1.2s',
        confidenceScore: 94,
        processingQueue: 3,
        escalations: 1,
        responseAccuracy: 96
      });

    } catch (error) {
      console.error('Failed to load AI management data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Enhance conversation with AI analysis
  const enhanceConversation = (conv: any): EnhancedConversation => {
    return {
      ...conv,
      aiAnalysis: conv.classification ? {
        sentiment: extractSentiment(conv.classification),
        intent: extractIntent(conv.classification),
        confidence: conv.confidence || 0,
        entities: conv.classification.entities || [],
        businessContext: conv.classification.businessContext || {},
        riskFactors: assessRiskFactors(conv)
      } : undefined
    };
  };

  // WebSocket event handlers
  function handleConversationUpdate(data: any) {
    setState(prev => ({
      ...prev,
      conversations: prev.conversations.map(conv => 
        conv.conversationId === data.conversationId ? enhanceConversation({ ...conv, ...data }) : conv
      ),
      lastUpdate: new Date(),
      pendingUpdates: prev.pendingUpdates + 1
    }));
  }

  function handleResponseGenerated(data: any) {
    setState(prev => ({
      ...prev,
      conversations: prev.conversations.map(conv => 
        conv.conversationId === data.conversationId 
          ? { ...conv, suggestedResponses: data.responses, status: 'needs-response' }
          : conv
      )
    }));
  }

  function handleEscalationTriggered(data: any) {
    setState(prev => ({
      ...prev,
      conversations: prev.conversations.map(conv => 
        conv.conversationId === data.conversationId 
          ? { ...conv, status: 'escalated', escalationRecommendation: data.escalation }
          : conv
      )
    }));
  }

  function handleDashboardStats(data: any) {
    setDashboardStats(data);
    setState(prev => ({
      ...prev,
      analytics: data.stats,
      performanceMetrics: data.aiMetrics,
      lastUpdate: new Date()
    }));
  }

  // Filter conversations
  const filteredConversations = state.conversations.filter(conversation => {
    const matchesSearch = 
      (conversation.contact?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (conversation.contact?.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (conversation.lastMessage?.content || '').toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilters = 
      (state.filters.status.length === 0 || state.filters.status.includes(conversation.status)) &&
      (state.filters.priority.length === 0 || state.filters.priority.includes(conversation.priority)) &&
      (!state.filters.requiresAttention || conversation.status === 'needs-response' || conversation.status === 'high-priority') &&
      (!state.filters.aiHandled || conversation.metadata?.processedBy === 'ai') &&
      (!state.filters.humanReviewRequired || conversation.status === 'needs-response');

    return matchesSearch && matchesFilters;
  });

  // Handle conversation selection
  const handleConversationSelect = (conversation: EnhancedConversation) => {
    setState(prev => ({ ...prev, selectedConversation: conversation }));
    joinConversation(conversation.conversationId);
  };

  // Handle AI response generation
  const handleGenerateResponse = async (preferences?: any) => {
    if (!state.selectedConversation) return;

    setState(prev => ({
      ...prev,
      conversations: prev.conversations.map(conv => 
        conv.id === state.selectedConversation?.id 
          ? { ...conv, status: 'ai-processing' as const }
          : conv
      )
    }));

    await generateResponse(state.selectedConversation.conversationId, {}, preferences);
  };

  // Handle response sending
  const handleSendResponse = async (responseId: string, customText?: string) => {
    if (!state.selectedConversation) return;

    await sendResponse(state.selectedConversation.conversationId, responseId, customText);

    setState(prev => ({
      ...prev,
      conversations: prev.conversations.map(conv => 
        conv.id === state.selectedConversation?.id 
          ? { ...conv, status: 'active' as const }
          : conv
      )
    }));
  };

  // Handle escalation request
  const handleRequestEscalation = async (reason: string, urgency: 'low' | 'medium' | 'high' | 'critical') => {
    if (!state.selectedConversation) return;

    await requestEscalation(state.selectedConversation.conversationId, reason, urgency);
  };

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Real-time stats for metric cards
  const realTimeStats = dashboardStats?.stats || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2 flex items-center">
              <Bot className="w-8 h-8 mr-3 text-cyan-400" />
              AI Response Management
            </h1>
            <p className="text-xl text-neutral-400 flex items-center">
              Intelligent conversation management with real-time AI assistance
              {isConnected ? (
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="ml-2"
                >
                  <Wifi className="w-4 h-4 text-green-400" />
                </motion.div>
              ) : (
                <WifiOff className="w-4 h-4 ml-2 text-red-400" />
              )}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
              onClick={() => setState(prev => ({ ...prev, selectedTab: 'performance' }))}
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              Analytics
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <Download className="w-5 h-5 mr-2" />
              Export
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
              onClick={loadConversations}
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Refresh
            </Button>
            <Button 
              size="lg"
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              onClick={handleGenerateResponse}
              disabled={!state.selectedConversation}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Generate AI Response
            </Button>
          </div>
        </motion.div>

        {/* Enhanced Stats Cards with Real-time Updates */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <EnhancedMetricCard
            title="AI Response Rate"
            value={`${realTimeStats.aiHandledPercentage || 0}%`}
            change="+12% vs last week"
            changeType="up"
            icon={Bot}
            gradient="bg-gradient-to-r from-cyan-500 to-blue-500"
            description="AI-powered automation coverage"
            realTime={state.realTimeEnabled}
            onClick={() => setState(prev => ({ ...prev, selectedTab: 'performance' }))}
          />
          <EnhancedMetricCard
            title="Active Conversations"
            value={realTimeStats.activeConversations || 0}
            change="+8% vs last week"
            changeType="up"
            icon={MessageSquare}
            gradient="bg-gradient-to-r from-green-500 to-emerald-500"
            description="Currently ongoing conversations"
            realTime={state.realTimeEnabled}
            onClick={() => setState(prev => ({ ...prev, filters: { ...prev.filters, status: ['active'] } }))}
          />
          <EnhancedMetricCard
            title="Needs Attention"
            value={realTimeStats.needsAttention || 0}
            change="2 new today"
            changeType="up"
            icon={AlertCircle}
            gradient="bg-gradient-to-r from-red-500 to-orange-500"
            description="Requires immediate action"
            realTime={state.realTimeEnabled}
            onClick={() => setState(prev => ({ ...prev, filters: { ...prev.filters, requiresAttention: true } }))}
          />
          <EnhancedMetricCard
            title="Avg Response Time"
            value={`${realTimeStats.averageResponseTime || 0}s`}
            change="-25% improvement"
            changeType="down"
            icon={Zap}
            gradient="bg-gradient-to-r from-purple-500 to-pink-500"
            description="AI-accelerated response times"
            realTime={state.realTimeEnabled}
          />
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[calc(100vh-500px)]"
        >
          {/* Conversations List with Enhanced Filters */}
          <div className="lg:col-span-1 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl shadow-xl overflow-hidden">
            {/* Search and Filters */}
            <div className="p-4 border-b border-neutral-700/50">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search conversations..."
                  className="w-full pl-9 pr-4 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all text-sm"
                />
              </div>

              <ConversationFilters
                filters={state.filters}
                conversations={state.conversations}
                onFiltersChange={(newFilters) => setState(prev => ({ ...prev, filters: newFilters }))}
              />
            </div>
          
            {/* Conversations List */}
            <div className="overflow-y-auto h-full">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-neutral-400 text-sm">Loading conversations...</p>
                  </div>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-12">
                  <Bot className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No conversations found</h3>
                  <p className="text-neutral-400 text-sm">
                    {searchQuery ? 'Try adjusting your search criteria' : 'Start by creating your first outreach campaign'}
                  </p>
                </div>
              ) : (
                <AnimatePresence>
                  {filteredConversations.map((conversation) => (
                    <motion.div
                      key={conversation.id}
                      layout
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                    >
                      <EnhancedConversationCard
                        conversation={conversation}
                        isSelected={state.selectedConversation?.id === conversation.id}
                        onClick={() => handleConversationSelect(conversation)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              )}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Conversation Detail */}
            <div className="lg:col-span-2 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl shadow-xl overflow-hidden">
              {state.selectedConversation ? (
                <RealTimeConversationView
                  conversation={state.selectedConversation}
                  onGenerateResponse={handleGenerateResponse}
                  onSendResponse={handleSendResponse}
                  onRequestEscalation={handleRequestEscalation}
                />
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Brain className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">AI Ready</h3>
                    <p className="text-neutral-400">
                      Select a conversation to start AI-powered response management
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* AI Response Panel / Analytics */}
            <div className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl shadow-xl overflow-hidden">
              {/* Tab Navigation */}
              <div className="border-b border-neutral-700/50 p-4">
                <div className="flex space-x-1">
                  {[
                    { id: 'responses', label: 'AI Responses', icon: Sparkles },
                    { id: 'analysis', label: 'Analysis', icon: Brain },
                    { id: 'performance', label: 'Performance', icon: BarChart3 }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setState(prev => ({ ...prev, selectedTab: tab.id as any }))}
                      className={cn(
                        'px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 flex items-center',
                        state.selectedTab === tab.id
                          ? 'bg-blue-500 text-white shadow-lg'
                          : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                      )}
                    >
                      <tab.icon className="w-3 h-3 mr-1" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="h-full overflow-y-auto">
                {state.selectedTab === 'responses' && state.selectedConversation && (
                  <AIResponsePanel
                    conversation={state.selectedConversation}
                    onResponseSelect={(response) => console.log('Response selected:', response)}
                    onResponseEdit={(response) => console.log('Response edited:', response)}
                    onResponseSend={handleSendResponse}
                  />
                )}
                {state.selectedTab === 'analysis' && state.selectedConversation && (
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                      <Brain className="w-5 h-5 mr-2 text-cyan-400" />
                      AI Analysis
                    </h3>
                    {state.selectedConversation.aiAnalysis ? (
                      <div className="space-y-4">
                        <div>
                          <p className="text-neutral-400 text-sm mb-1">Sentiment</p>
                          <p className="text-white capitalize">{state.selectedConversation.aiAnalysis.sentiment}</p>
                        </div>
                        <div>
                          <p className="text-neutral-400 text-sm mb-1">Intent</p>
                          <p className="text-white capitalize">{state.selectedConversation.aiAnalysis.intent}</p>
                        </div>
                        <div>
                          <p className="text-neutral-400 text-sm mb-1">Confidence</p>
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-neutral-700 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-cyan-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${state.selectedConversation.aiAnalysis.confidence * 100}%` }}
                              />
                            </div>
                            <span className="text-white text-sm">
                              {Math.round(state.selectedConversation.aiAnalysis.confidence * 100)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-neutral-400">No AI analysis available</p>
                    )}
                  </div>
                )}
                {state.selectedTab === 'performance' && (
                  <ConversationAnalytics
                    analytics={state.analytics}
                    performanceMetrics={state.performanceMetrics}
                  />
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// Helper functions
function extractSentiment(classification: any): string {
  if (!classification || !classification.sentiment) return 'neutral';
  
  const sentiment = classification.sentiment;
  if (typeof sentiment === 'object' && sentiment.type) {
    return sentiment.type.includes('positive') ? 'positive' : 
           sentiment.type.includes('negative') ? 'negative' : 'neutral';
  }
  
  return 'neutral';
}

function extractIntent(classification: any): string {
  if (!classification || !classification.intent) return 'unknown';
  
  const intent = classification.intent;
  if (typeof intent === 'object' && intent.type) {
    return intent.type;
  }
  
  return 'unknown';
}

function assessRiskFactors(conversation: any): string[] {
  const risks = [];
  
  if (conversation.shouldEscalate) risks.push('High escalation risk');
  if (conversation.confidence < 0.5) risks.push('Low AI confidence');
  if (conversation.humanReviewRequired) risks.push('Requires human review');
  
  const sentiment = extractSentiment(conversation.classification);
  if (sentiment === 'negative') risks.push('Negative sentiment');
  
  return risks;
} 